<template>
	<div class="tw-flex tw-gap-1" :class="customClasses">
		<div
			class="tw-flex tw-items-center tw-justify-center tw-ring-gray-400/[.14]"
			:style="computedSizeStyles"
			:class="computedClasses"
			@click="toggle"
		>
			<InlineSvg
				v-if="currentIcon"
				:src="currentIcon"
				:width="iconSizes[props.size]"
				:height="iconSizes[props.size]"
				:class="iconClasses"
			/>
		</div>
		<slot name="label" v-bind="{ toggle }">
			<div
				v-if="label"
				:class="{ 'tw-cursor-pointer': !disabled, 'tw-cursor-not-allowed': disabled }"
				@click="toggle"
			>
				{{ label }}
			</div>
		</slot>
	</div>
</template>

<script setup>
import InlineSvg from '../inline-svg';
import { loadFile } from '@/v2/utils/index';
import { computed } from 'vue';

const props = defineProps({
	modelValue: {},
	trueValue: {
		type: [String, Boolean, Number],
		default: true,
	},
	falseValue: {
		type: [String, Boolean, Number],
		default: false,
	},
	size: {
		type: String,
		default: 'md',
	},
	intermediate: {
		type: Boolean,
		default: false,
	},
	disabled: {
		type: Boolean,
		default: false,
	},
	label: {
		type: String,
		default: '',
	},
	customClasses: {
		type: String,
		default: 'tw-items-center',
	},
	checked: {
		type: Boolean,
		default: false
	}
});

const emit = defineEmits(['update:modelValue']);

const checkedIcon = loadFile('assets/v2/image/icons/check.svg');
const minusIcon = loadFile('assets/v2/image/icons/minus.svg');

const isChecked = computed(() => {
	if (!!props.checked) return true;
	
	if (Array.isArray(props.modelValue)) {
		return props.modelValue.some((val) => val === props.trueValue);
	} else {
		return props.modelValue === props.trueValue;
	}
});

const toggle = () => {
	if (props.disabled) return;
	if (Array.isArray(props.modelValue)) {
		if (isChecked.value || props.intermediate) {
			const idx = props.modelValue.findIndex((el) => el === props.trueValue);
			const res = [...props.modelValue];
			res.splice(idx, 1);
			emit('update:modelValue', res);
		} else {
			emit('update:modelValue', [...props.modelValue, props.trueValue]);
		}
	} else {
		if (isChecked.value || props.intermediate) {
			emit('update:modelValue', props.falseValue);
		} else {
			emit('update:modelValue', props.trueValue);
		}
	}
};

const currentIcon = computed(() => {
	if (props.intermediate) return minusIcon;
	if (isChecked.value) return checkedIcon;
});

const computedClasses = computed(() => {
	const res = [];
	if (!props.disabled && (isChecked.value || props.intermediate)) {
		res.push('tw-bg-brand');
	}
	if (props.disabled) {
		res.push('tw-bg-brand-50 tw-border-brand-200');
	} else if (!isChecked.value && !props.intermediate) {
		res.push('tw-border tw-border-gray-300');
	}
	if (!props.disabled) {
		res.push('tw-cursor-pointer active:tw-ring');
	} else {
		res.push('tw-cursor-not-allowed');
	}
    if(!currentIcon.value) res.push('tw-bg-white')
	return res;
});

const iconClasses = computed(() => {
	if (props.disabled) {
		return 'tw-text-brand-300';
	}
	return 'tw-text-white';
});

const computedSizeStyles = computed(() => {
	return sizeStyles[props.size];
});

const sizeStyles = {
	sm: { width: '16px', height: '16px', 'border-radius': '4px' },
	md: { width: '20px', height: '20px', 'border-radius': '6px' },
};
const iconSizes = {
	sm: '12px',
	md: '14px',
};

defineExpose({
	toggle,
});
</script>

<style lang="scss" scoped>
@import 'tailwindcss/base';
@import 'tailwindcss/utilities';
</style>
